import React, { useEffect, useState } from "react";
import CountUp from "react-countup";
import { Tree, Waves, Recycle, SunHorizon, Medal, Wind } from "@phosphor-icons/react";
import { DASHBOARD } from "@/constants/testIds";

const items = [
    { key: "trees_restored", label: "Trees Restored", suffix: "", icon: Tree, color: "#a3e635", glow: "eviq-glow-green" },
    { key: "ocean_plastic_lbs", label: "Ocean Plastic Removed", suffix: "lbs", icon: Waves, color: "#06b6d4", glow: "eviq-glow-ocean" },
    { key: "batteries_diverted", label: "Batteries Diverted from Landfill", suffix: "", icon: Recycle, color: "#8b5cf6", glow: "" },
    { key: "schools_supported", label: "Schools Supported", suffix: "", icon: SunHorizon, color: "#fbbf24", glow: "" },
    { key: "veterans_sponsored", label: "Veterans Sponsored", suffix: "", icon: Medal, color: "#f59e0b", glow: "eviq-glow-veteran" },
    { key: "co2_prevented_lbs", label: "CO₂ Prevented", suffix: "lbs", icon: Wind, color: "#a3e635", glow: "" },
];

export const CommunityTracker = ({ initial, rates }) => {
    const [live, setLive] = useState(initial);

    useEffect(() => {
        if (!initial || !rates) return;
        const start = Date.now();
        const startValues = { ...initial };
        const tick = () => {
            const elapsed = (Date.now() - start) / 1000;
            const next = {};
            for (const [k, v] of Object.entries(startValues)) {
                const rate = rates[k] ?? 0;
                if (typeof v === "number") {
                    next[k] = v + rate * elapsed;
                } else {
                    next[k] = v;
                }
            }
            setLive(next);
        };
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [initial, rates]);

    if (!live) return null;

    return (
        <section
            data-testid={DASHBOARD.community}
            className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 sm:p-8 lg:p-10"
        >
            <div className="mb-6 flex flex-col gap-3 sm:mb-8 md:flex-row md:items-end md:justify-between">
                <div>
                    <span className="eviq-chip">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#a3e635] animate-pulse" />
                        Live · Community Impact
                    </span>
                    <h2 className="mt-4 font-editorial text-2xl leading-tight text-white sm:text-3xl md:text-4xl">
                        The EVIQ community, at this moment.
                    </h2>
                    <p className="mt-2 max-w-lg text-sm text-zinc-500">
                        Every counter grows in real time as more drivers plug into EVIQ.
                        No donation prompts. Just outcomes.
                    </p>
                </div>
                <span className="font-mono-metric text-[10px] uppercase tracking-[0.28em] text-zinc-500">
                    updates every second
                </span>
            </div>

            <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-[#1f1f22] bg-[#1f1f22] md:grid-cols-2 xl:grid-cols-3">
                {items.map((it) => {
                    const Icon = it.icon;
                    const val = live[it.key] ?? 0;
                    const isDecimal = it.key === "battery_life_extended_years";
                    return (
                        <div
                            key={it.key}
                            data-testid={DASHBOARD.communityMetric(it.key)}
                            className={`group relative bg-[#0a0a0a] p-6 transition-colors duration-300 hover:bg-[#0e0e0e] sm:p-8`}
                        >
                            <div className="flex items-center justify-between">
                                <div className={`flex h-10 w-10 items-center justify-center rounded-md border border-[#1f1f22] bg-[#050505] ${it.glow}`}>
                                    <Icon size={18} color={it.color} weight="fill" />
                                </div>
                                <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-600">
                                    {it.suffix || "count"}
                                </span>
                            </div>
                            <div className="mt-8">
                                <div className="font-mono-metric text-3xl font-bold text-white sm:text-4xl md:text-5xl">
                                    <CountUp
                                        end={val}
                                        duration={1.2}
                                        separator=","
                                        decimals={isDecimal ? 1 : 0}
                                        preserveValue
                                    />
                                </div>
                                <p className="mt-2 text-sm text-zinc-400">{it.label}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

export default CommunityTracker;
